/**
 * Local Countdown Designs catalog — 10 hand-crafted aesthetic countdown-card
 * designs for the Countdown widget "design store" (mirrors the Timer store
 * flow — see `timerDesignsCatalog.ts`, the proven reference).
 *
 * Source: the Claude Design pack
 * "eriol-overlay-variants-intermission/project/Countdown Widgets.html" — 10
 * full 1920×1080 SVG <template id="cd-*"> scenes whose countdown card lives
 * bottom-left in `<g transform="translate(110 720)">` as a ~700–760 × 300 card
 * showing FOUR D/H/M/S unit blocks (numbers 02 / 14 / 37 / 09 with labels
 * DÍAS/HRS/MIN/SEG).
 *
 * RENDER MODEL (fixed user decision): we use ONLY the card of each design (not
 * the full scene). Each `frame` below is the verbatim inner content of that
 * design's `<g transform="translate(110 720)">` (the translate is DROPPED so the
 * card sits at 0,0), PLUS the template's `<defs>`, re-wrapped in an
 * `<svg viewBox="0 0 cardWidth cardHeight">` sized to that design's card.
 *
 * PURE-DATA format: every design is now a {@link SerializableDesign} — plain
 * data, no JS `build()`/static-svg coupling — so designs can be stored,
 * transmitted and safely PUBLISHED from the community marketplace. The generic
 * {@link renderDesign} turns the data into the final SVG; `renderCountdownDesignSvg`
 * is the thin adapter every caller (Store thumbnail, config preview, live Pixi
 * rasteriser) keeps calling with the same `{dd,hh,mm,ss}` signature.
 *
 * Pixel-perfect rule: the SVG IS the design — the markup is reused verbatim
 * EXCEPT for two mechanical transforms:
 *   1. `<defs>` ids (gradients / patterns / filters like `cdd-dots`, `cdf-tile`,
 *      `cdy-bg`/`cdy-cap`, `cdc-g`, `cdco-linen`/`cdco-st`, `cda-rgb`/`cda-scan`,
 *      `cdh-foil`/`cdh-streak`) AND the reusable `<g id="cd?-cell">` /
 *      `<use href="#cd?-cell">` blocks collide across designs when several cards
 *      are inlined on one page (the gallery). Every defs/cell id is PREFIXED
 *      with the design id (e.g. `cdd-dots` → `dark-cdd-dots`) and every
 *      `url(#…)` / `href="#…"` / `filter="url(#…)"` reference is updated to
 *      match.
 *   2. The four big unit numbers are `{{DD}}` / `{{HH}}` / `{{MM}}` / `{{SS}}`
 *      placeholders, in D,H,M,S order: days(02) → `{{DD}}`, hours(14) →
 *      `{{HH}}`, minutes(37) → `{{MM}}`, seconds(09) → `{{SS}}`. The
 *      DÍAS/HRS/MIN/SEG labels are kept as-is. They are backed by `time` slots
 *      (system-generated zero-padded strings — never XML-escaped). Designs that
 *      colour the SEG block differently keep that styling verbatim.
 *
 * fonts = the Google Fonts that design's SVG references (so the live Pixi
 * rasteriser / DOM preview can ensure they are loaded before drawing).
 */

import { renderDesign } from "../entities/designs/renderDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";
import type {
  DesignSlot,
  SerializableDesign,
} from "../entities/designs/serializableDesign";

export type CountdownDesignCategory = "countdown";

/**
 * A countdown design is a {@link SerializableDesign} narrowed to the countdown
 * widget. The four unit numbers are filled from the `DD`/`HH`/`MM`/`SS` `time`
 * slots; all other markup is static chrome in `frame`.
 */
export interface CountdownDesign extends SerializableDesign {
  widgetType: "countdown";
  category: CountdownDesignCategory;
}

/** The four countdown values, already zero-padded strings (e.g. "02"). */
export interface CountdownParts {
  dd: string;
  hh: string;
  mm: string;
  ss: string;
}

/**
 * The D/H/M/S unit numbers are produced by `getTimeParts` + zero-pad — purely
 * system-generated numeric strings, never user input — so they are NOT escaped
 * (kept as `time` slots, escape:false, no cap), preserving the verbatim digits.
 */
const TIME_SLOTS: Record<string, DesignSlot> = {
  DD: { kind: "time", escape: false },
  HH: { kind: "time", escape: false },
  MM: { kind: "time", escape: false },
  SS: { kind: "time", escape: false },
};

// ---------------------------------------------------------------------------
// 01 — Dark Minimal (countdown)  ·  card 700×300
// defs: cdd-dots
// ---------------------------------------------------------------------------

const darkFrame = `<svg viewBox="0 0 700 300" xmlns="http://www.w3.org/2000/svg">
  <defs><pattern id="dark-cdd-dots" width="22" height="22" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1.1" fill="#33333d" opacity=".6"/></pattern></defs>
    <rect width="700" height="300" rx="22" fill="#0d0d12" stroke="#2a2a33" stroke-width="1.5"/>
    <rect width="700" height="300" rx="22" fill="url(#dark-cdd-dots)"/>
    <text x="40" y="78" font-family="Inter, sans-serif" font-weight="700" font-size="22" fill="#cfd6e2" letter-spacing="8">FALTAN PARA EL ESTRENO</text>
    <text x="660" y="78" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#3ad96b" letter-spacing="4">● COUNTDOWN</text>
    <g transform="translate(40 110)">
      <g><rect width="142" height="150" rx="14" fill="#15151d" stroke="#26262f" stroke-width="1"/><text x="71" y="100" text-anchor="middle" font-family="DM Mono, monospace" font-size="72" fill="#f5f5f5">{{DD}}</text><text x="71" y="130" text-anchor="middle" font-family="Inter, sans-serif" font-weight="600" font-size="14" fill="#7fbfff" letter-spacing="4">DÍAS</text></g>
      <g transform="translate(154 0)"><rect width="142" height="150" rx="14" fill="#15151d" stroke="#26262f" stroke-width="1"/><text x="71" y="100" text-anchor="middle" font-family="DM Mono, monospace" font-size="72" fill="#f5f5f5">{{HH}}</text><text x="71" y="130" text-anchor="middle" font-family="Inter, sans-serif" font-weight="600" font-size="14" fill="#7fbfff" letter-spacing="4">HRS</text></g>
      <g transform="translate(308 0)"><rect width="142" height="150" rx="14" fill="#15151d" stroke="#26262f" stroke-width="1"/><text x="71" y="100" text-anchor="middle" font-family="DM Mono, monospace" font-size="72" fill="#f5f5f5">{{MM}}</text><text x="71" y="130" text-anchor="middle" font-family="Inter, sans-serif" font-weight="600" font-size="14" fill="#7fbfff" letter-spacing="4">MIN</text></g>
      <g transform="translate(462 0)"><rect width="142" height="150" rx="14" fill="#15151d" stroke="#3ad96b" stroke-width="1.5"/><text x="71" y="100" text-anchor="middle" font-family="DM Mono, monospace" font-size="72" fill="#3ad96b">{{SS}}</text><text x="71" y="130" text-anchor="middle" font-family="Inter, sans-serif" font-weight="600" font-size="14" fill="#3ad96b" letter-spacing="4">SEG</text></g>
    </g>
</svg>`;

// ---------------------------------------------------------------------------
// 02 — Flip Board (countdown)  ·  card 720×300
// defs: cdf-tile  ·  reusable cell: cdf-t (<use href>)
// ---------------------------------------------------------------------------

const flipFrame = `<svg viewBox="0 0 720 300" xmlns="http://www.w3.org/2000/svg">
  <defs><linearGradient id="flip-cdf-tile" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#33333d"/><stop offset="49%" stop-color="#26262e"/><stop offset="51%" stop-color="#1a1a20"/><stop offset="100%" stop-color="#202028"/></linearGradient></defs>
    <rect width="720" height="300" rx="20" fill="#15151a"/>
    <text x="360" y="52" text-anchor="middle" font-family="DM Mono, monospace" font-size="16" fill="#ffb703" letter-spacing="14">GRAN ESTRENO</text>
    <g transform="translate(40 80)">
      <g id="flip-cdf-t"><rect width="140" height="150" rx="12" fill="url(#flip-cdf-tile)" stroke="#0a0a0d" stroke-width="2"/><line x1="0" y1="75" x2="140" y2="75" stroke="#0a0a0d" stroke-width="3"/><rect x="6" y="6" width="128" height="66" rx="8" fill="#fff" opacity=".05"/><circle cx="-2" cy="75" r="5" fill="#0a0a0d"/><circle cx="142" cy="75" r="5" fill="#0a0a0d"/></g>
      <use href="#flip-cdf-t" transform="translate(160 0)"/>
      <use href="#flip-cdf-t" transform="translate(320 0)"/>
      <use href="#flip-cdf-t" transform="translate(480 0)"/>
      <g font-family="DM Mono, monospace" font-weight="500" font-size="86" fill="#f5f5f5" text-anchor="middle">
        <text x="70" y="118">{{DD}}</text><text x="230" y="118">{{HH}}</text><text x="390" y="118">{{MM}}</text><text x="550" y="118">{{SS}}</text>
      </g>
      <g font-family="DM Mono, monospace" font-size="14" fill="#ffb703" letter-spacing="4" text-anchor="middle"><text x="70" y="174">DÍAS</text><text x="230" y="174">HRS</text><text x="390" y="174">MIN</text><text x="550" y="174">SEG</text></g>
    </g>
</svg>`;

// ---------------------------------------------------------------------------
// 03 — Y2K Aqua Gel (countdown)  ·  card 720×300
// defs: cdy-bg, cdy-cap  ·  reusable cell: cdy-cell (<use href>)
// ---------------------------------------------------------------------------

const y2kFrame = `<svg viewBox="0 0 720 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="y2k-cdy-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeaff"/><stop offset="55%" stop-color="#1c8fff"/><stop offset="100%" stop-color="#003a7a"/></linearGradient>
    <linearGradient id="y2k-cdy-cap" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#f0fbff"/><stop offset="45%" stop-color="#8acfff"/><stop offset="100%" stop-color="#1c8fff"/></linearGradient>
  </defs>
    <rect width="720" height="300" rx="40" fill="url(#y2k-cdy-bg)" stroke="#fff" stroke-width="3"/>
    <ellipse cx="360" cy="32" rx="320" ry="14" fill="#fff" opacity=".7"/>
    <g transform="translate(40 40)"><circle r="10" fill="#003a7a"/><circle r="6" fill="#5aff8a"/></g>
    <text x="62" y="46" font-family="Audiowide, sans-serif" font-size="16" fill="#cffff4" letter-spacing="8">CUENTA REGRESIVA</text>
    <g transform="translate(40 86)">
      <g id="y2k-cdy-cell"><rect width="150" height="170" rx="28" fill="url(#y2k-cdy-cap)" stroke="#fff" stroke-width="2"/><ellipse cx="75" cy="22" rx="58" ry="8" fill="#fff" opacity=".7"/></g>
      <use href="#y2k-cdy-cell" transform="translate(166 0)"/>
      <use href="#y2k-cdy-cell" transform="translate(332 0)"/>
      <use href="#y2k-cdy-cell" transform="translate(498 0)"/>
      <g font-family="Audiowide, sans-serif" font-size="64" fill="#fff" text-anchor="middle"><text x="75" y="106">{{DD}}</text><text x="241" y="106">{{HH}}</text><text x="407" y="106">{{MM}}</text><text x="573" y="106">{{SS}}</text></g>
      <g font-family="DM Mono, monospace" font-size="13" fill="#003a7a" letter-spacing="4" text-anchor="middle"><text x="75" y="146">DÍAS</text><text x="241" y="146">HRS</text><text x="407" y="146">MIN</text><text x="573" y="146">SEG</text></g>
    </g>
</svg>`;

// ---------------------------------------------------------------------------
// 04 — Cyberpunk Neon (countdown)  ·  card 760×300 (polygon to 760)
// defs: cdc-g (filter on the group)  ·  reusable cell: cdc-cell (<use href>)
// note: the SEG number + label are coloured pink (#ff2e88 / #ff7ac8) — verbatim.
// ---------------------------------------------------------------------------

const cyberFrame = `<svg viewBox="0 0 760 300" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="cyber-cdc-g"><feGaussianBlur stdDeviation="2.5"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
  <g filter="url(#cyber-cdc-g)">
    <polygon points="0,40 40,0 720,0 760,40 760,260 720,300 40,300 0,260" fill="#080010" stroke="#ff2e88" stroke-width="2.5"/>
    <polygon points="14,46 50,14 710,14 746,46 746,254 710,286 50,286 14,254" fill="none" stroke="#00f0ff" stroke-width="1.5" opacity=".7"/>
    <text x="34" y="44" font-family="DM Mono, monospace" font-size="13" fill="#f5d019" letter-spacing="8">▮ T-MINUS · 起動カウント</text>
    <text x="726" y="44" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#ff2e88" letter-spacing="6">● ARMED</text>
    <g transform="translate(34 80)">
      <g id="cyber-cdc-cell"><polygon points="0,12 12,0 150,0 162,12 162,150 150,162 12,162 0,150" fill="#0a0014" stroke="#00f0ff" stroke-width="1.5"/></g>
      <use href="#cyber-cdc-cell" transform="translate(178 0)"/>
      <use href="#cyber-cdc-cell" transform="translate(356 0)"/>
      <use href="#cyber-cdc-cell" transform="translate(534 0)"/>
      <g font-family="Orbitron, sans-serif" font-weight="900" font-size="56" fill="#00f0ff" text-anchor="middle"><text x="81" y="100">{{DD}}</text><text x="259" y="100">{{HH}}</text><text x="437" y="100">{{MM}}</text><text x="615" y="100" fill="#ff2e88">{{SS}}</text></g>
      <g font-family="DM Mono, monospace" font-size="12" fill="#88e8d8" letter-spacing="6" text-anchor="middle"><text x="81" y="142">DÍAS</text><text x="259" y="142">HRS</text><text x="437" y="142">MIN</text><text x="615" y="142" fill="#ff7ac8">SEG</text></g>
    </g>
  </g>
</svg>`;

// ---------------------------------------------------------------------------
// 05 — Editorial (countdown)  ·  card 700×300  ·  no defs
// note: the SEG number is coloured #8a3a2a — verbatim.
// ---------------------------------------------------------------------------

const editorialFrame = `<svg viewBox="0 0 700 300" xmlns="http://www.w3.org/2000/svg">
    <rect width="700" height="300" fill="#f5efe2"/>
    <line x1="24" y1="22" x2="676" y2="22" stroke="#1a1815" stroke-width="3"/>
    <line x1="24" y1="30" x2="676" y2="30" stroke="#1a1815" stroke-width="1"/>
    <text x="350" y="70" text-anchor="middle" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="40" fill="#1a1815">Reservá la fecha</text>
    <text x="350" y="98" text-anchor="middle" font-family="DM Mono, monospace" font-size="12" fill="#8a3a2a" letter-spacing="10">GRAN ESTRENO · SÁB 21:00 · MMXXVI</text>
    <g transform="translate(40 120)" font-family="Cormorant Garamond, serif">
      <g><text x="75" y="80" text-anchor="middle" font-weight="600" font-size="72" fill="#1a1815">{{DD}}</text><line x1="20" y1="100" x2="130" y2="100" stroke="#c8a050" stroke-width="1.5"/><text x="75" y="128" text-anchor="middle" font-family="DM Mono, monospace" font-size="13" fill="#8a3a2a" letter-spacing="6">DÍAS</text></g>
      <g transform="translate(155 0)"><text x="75" y="80" text-anchor="middle" font-weight="600" font-size="72" fill="#1a1815">{{HH}}</text><line x1="20" y1="100" x2="130" y2="100" stroke="#c8a050" stroke-width="1.5"/><text x="75" y="128" text-anchor="middle" font-family="DM Mono, monospace" font-size="13" fill="#8a3a2a" letter-spacing="6">HRS</text></g>
      <g transform="translate(310 0)"><text x="75" y="80" text-anchor="middle" font-weight="600" font-size="72" fill="#1a1815">{{MM}}</text><line x1="20" y1="100" x2="130" y2="100" stroke="#c8a050" stroke-width="1.5"/><text x="75" y="128" text-anchor="middle" font-family="DM Mono, monospace" font-size="13" fill="#8a3a2a" letter-spacing="6">MIN</text></g>
      <g transform="translate(465 0)"><text x="75" y="80" text-anchor="middle" font-weight="600" font-size="72" fill="#8a3a2a">{{SS}}</text><line x1="20" y1="100" x2="130" y2="100" stroke="#c8a050" stroke-width="1.5"/><text x="75" y="128" text-anchor="middle" font-family="DM Mono, monospace" font-size="13" fill="#8a3a2a" letter-spacing="6">SEG</text></g>
      <line x1="150" y1="20" x2="150" y2="120" stroke="#1a1815" stroke-width="0.5"/><line x1="305" y1="20" x2="305" y2="120" stroke="#1a1815" stroke-width="0.5"/><line x1="460" y1="20" x2="460" y2="120" stroke="#1a1815" stroke-width="0.5"/>
    </g>
</svg>`;

// ---------------------------------------------------------------------------
// 06 — Pixel Arcade (countdown)  ·  card 720×300  ·  no defs
// note: the SEG cell stroke + number are coloured #ff3d4d — verbatim.
// ---------------------------------------------------------------------------

const pixelFrame = `<svg viewBox="0 0 720 300" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
    <rect width="720" height="300" fill="#48cae4"/>
    <rect x="8" y="8" width="704" height="284" fill="#1d2a3a"/>
    <rect x="16" y="16" width="688" height="268" fill="#0a121c"/>
    <g fill="#ffb703"><rect width="24" height="24"/><rect x="696" width="24" height="24"/><rect y="276" width="24" height="24"/><rect x="696" y="276" width="24" height="24"/></g>
    <g transform="translate(40 -16)"><rect width="280" height="32" fill="#ff3d4d"/><rect x="6" y="6" width="268" height="20" fill="#1d2a3a"/><text x="140" y="22" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="12" fill="#ffb703" letter-spacing="2">★ NEXT EVENT ★</text></g>
    <g transform="translate(40 70)" font-family="Press Start 2P, monospace" text-anchor="middle">
      <g><rect width="148" height="160" fill="#1d2a3a" stroke="#48cae4" stroke-width="3"/><text x="74" y="96" font-size="34" fill="#ffb703">{{DD}}</text><text x="74" y="134" font-size="11" fill="#48cae4">DIAS</text></g>
      <g transform="translate(162 0)"><rect width="148" height="160" fill="#1d2a3a" stroke="#48cae4" stroke-width="3"/><text x="74" y="96" font-size="34" fill="#ffb703">{{HH}}</text><text x="74" y="134" font-size="11" fill="#48cae4">HRS</text></g>
      <g transform="translate(324 0)"><rect width="148" height="160" fill="#1d2a3a" stroke="#48cae4" stroke-width="3"/><text x="74" y="96" font-size="34" fill="#ffb703">{{MM}}</text><text x="74" y="134" font-size="11" fill="#48cae4">MIN</text></g>
      <g transform="translate(486 0)"><rect width="148" height="160" fill="#1d2a3a" stroke="#ff3d4d" stroke-width="3"/><text x="74" y="96" font-size="34" fill="#ff3d4d">{{SS}}</text><text x="74" y="134" font-size="11" fill="#48cae4">SEG</text></g>
    </g>
</svg>`;

// ---------------------------------------------------------------------------
// 07 — Cottagecore (countdown)  ·  card 720×300
// defs: cdco-linen, cdco-st  ·  reusable cell: cdco-page (<use href>)
// note: the SEG number is coloured #b85a4a — verbatim.
// ---------------------------------------------------------------------------

const cottageFrame = `<svg viewBox="0 0 720 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="cottage-cdco-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="cottage-cdco-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
  </defs>
    <rect width="720" height="300" fill="url(#cottage-cdco-linen)" stroke="#6b4a2a" stroke-width="2"/>
    <rect width="720" height="24" fill="url(#cottage-cdco-st)"/>
    <rect y="276" width="720" height="24" fill="url(#cottage-cdco-st)"/>
    <rect width="24" height="300" fill="url(#cottage-cdco-st)"/>
    <rect x="696" width="24" height="300" fill="url(#cottage-cdco-st)"/>
    <text x="360" y="66" text-anchor="middle" font-family="DM Mono, monospace" font-size="13" fill="#b85a4a" letter-spacing="12">— falta poquito para el estreno —</text>
    <g transform="translate(48 86)" font-family="Cormorant Garamond, serif" text-anchor="middle">
      <g id="cottage-cdco-page"><rect width="140" height="160" rx="8" fill="#fff8e8" stroke="#6b4a2a" stroke-width="2"/><rect width="140" height="30" rx="8" fill="#b85a4a"/><circle cx="40" cy="6" r="5" fill="#6b4a2a"/><circle cx="100" cy="6" r="5" fill="#6b4a2a"/></g>
      <use href="#cottage-cdco-page" transform="translate(156 0)"/>
      <use href="#cottage-cdco-page" transform="translate(312 0)"/>
      <use href="#cottage-cdco-page" transform="translate(468 0)"/>
      <g font-style="italic" font-weight="600" font-size="58" fill="#2a1a08"><text x="70" y="116">{{DD}}</text><text x="226" y="116">{{HH}}</text><text x="382" y="116">{{MM}}</text><text x="538" y="116" fill="#b85a4a">{{SS}}</text></g>
      <g font-family="DM Mono, monospace" font-size="12" fill="#5a7a4a" letter-spacing="4"><text x="70" y="148">DÍAS</text><text x="226" y="148">HRS</text><text x="382" y="148">MIN</text><text x="538" y="148">SEG</text></g>
    </g>
</svg>`;

// ---------------------------------------------------------------------------
// 08 — Brutalist (countdown)  ·  card 720×300  ·  no defs
// note: the SEG number is coloured #ff5a2a — verbatim.
// ---------------------------------------------------------------------------

const brutalistFrame = `<svg viewBox="0 0 720 300" xmlns="http://www.w3.org/2000/svg">
    <rect width="720" height="300" fill="#1a1a1a"/>
    <rect width="24" height="300" fill="#ff5a2a"/>
    <text x="48" y="60" font-family="DM Mono, monospace" font-size="14" fill="#ff5a2a" letter-spacing="14">// FALTA PARA EL ESTRENO</text>
    <g transform="translate(48 96)" font-family="Anton, sans-serif" fill="#f5f5f5" text-anchor="middle">
      <text x="80" y="120" font-size="110" letter-spacing="-4">{{DD}}</text>
      <text x="246" y="120" font-size="110" letter-spacing="-4">{{HH}}</text>
      <text x="412" y="120" font-size="110" letter-spacing="-4">{{MM}}</text>
      <text x="578" y="120" font-size="110" letter-spacing="-4" fill="#ff5a2a">{{SS}}</text>
      <g font-family="DM Mono, monospace" font-size="14" fill="#c8c2b8" letter-spacing="6"><text x="80" y="160">DÍAS</text><text x="246" y="160">HRS</text><text x="412" y="160">MIN</text><text x="578" y="160">SEG</text></g>
    </g>
    <g stroke="#3a3a3a"><line x1="178" y1="100" x2="178" y2="270"/><line x1="344" y1="100" x2="344" y2="270"/><line x1="510" y1="100" x2="510" y2="270"/></g>
</svg>`;

// ---------------------------------------------------------------------------
// 09 — Anime VHS (countdown)  ·  card 726×300 (pink card + cyan ghost at x=6)
// defs: cda-rgb (filter on the group), cda-scan  ·  reusable cell: cda-cell
// note: the SEG number is coloured #ff7ad0 — verbatim.
// ---------------------------------------------------------------------------

const animeFrame = `<svg viewBox="0 0 726 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="anime-cda-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="anime-cda-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".15"/></pattern>
  </defs>
  <g filter="url(#anime-cda-rgb)">
    <rect x="6" y="0" width="720" height="300" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".55"/>
    <rect width="720" height="300" fill="#0a0218" stroke="#ff7ad0" stroke-width="4"/>
    <rect x="10" y="10" width="700" height="280" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".55"/>
    <rect width="720" height="300" fill="url(#anime-cda-scan)"/>
    <g><rect width="260" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="4">カウントダウン · 第043話</text></g>
    <g transform="translate(40 80)" font-family="VT323, monospace" text-anchor="middle">
      <g id="anime-cda-cell"><rect width="148" height="160" fill="#1a0a2a" stroke="#7ad0ff" stroke-width="2"/></g>
      <use href="#anime-cda-cell" transform="translate(162 0)"/>
      <use href="#anime-cda-cell" transform="translate(324 0)"/>
      <use href="#anime-cda-cell" transform="translate(486 0)"/>
      <g font-size="70" fill="#fff7c2"><text x="74" y="104">{{DD}}</text><text x="236" y="104">{{HH}}</text><text x="398" y="104">{{MM}}</text><text x="560" y="104" fill="#ff7ad0">{{SS}}</text></g>
      <g font-size="20" fill="#7ad0ff"><text x="74" y="144">DÍAS</text><text x="236" y="144">HRS</text><text x="398" y="144">MIN</text><text x="560" y="144">SEG</text></g>
    </g>
  </g>
</svg>`;

// ---------------------------------------------------------------------------
// 10 — Holographic Foil (countdown)  ·  card 720×300
// defs: cdh-foil, cdh-streak  ·  reusable cell: cdh-cell (<use href>)
// ---------------------------------------------------------------------------

const holoFrame = `<svg viewBox="0 0 720 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="holo-cdh-foil" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#f0c2ff"/><stop offset="25%" stop-color="#c2e8ff"/><stop offset="55%" stop-color="#c2ffd5"/><stop offset="80%" stop-color="#ffe0c2"/><stop offset="100%" stop-color="#f0c2ff"/></linearGradient>
    <linearGradient id="holo-cdh-streak" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#fff" stop-opacity="0"/><stop offset="50%" stop-color="#fff" stop-opacity=".75"/><stop offset="100%" stop-color="#fff" stop-opacity="0"/></linearGradient>
  </defs>
    <rect width="720" height="300" rx="22" fill="url(#holo-cdh-foil)" stroke="#fff" stroke-width="3"/>
    <rect x="20" y="44" width="680" height="14" fill="url(#holo-cdh-streak)" transform="skewY(-1.5)"/>
    <rect x="20" y="210" width="680" height="10" fill="url(#holo-cdh-streak)" transform="skewY(-1)"/>
    <text x="40" y="64" font-family="DM Mono, monospace" font-size="14" fill="#3a2a44" letter-spacing="10">▼ GRAN ESTRENO · COUNTDOWN</text>
    <g transform="translate(40 84)" font-family="Space Grotesk, sans-serif" text-anchor="middle" fill="#3a2a44">
      <g id="holo-cdh-cell"><rect width="150" height="150" rx="16" fill="#fff" opacity=".4"/></g>
      <use href="#holo-cdh-cell" transform="translate(166 0)"/>
      <use href="#holo-cdh-cell" transform="translate(332 0)"/>
      <use href="#holo-cdh-cell" transform="translate(498 0)"/>
      <g font-weight="700" font-size="64"><text x="75" y="96">{{DD}}</text><text x="241" y="96">{{HH}}</text><text x="407" y="96">{{MM}}</text><text x="573" y="96">{{SS}}</text></g>
      <g font-family="DM Mono, monospace" font-size="12" letter-spacing="6"><text x="75" y="130">DÍAS</text><text x="241" y="130">HRS</text><text x="407" y="130">MIN</text><text x="573" y="130">SEG</text></g>
    </g>
    <g transform="translate(680 56)" stroke="#fff" stroke-width="3" fill="none"><path d="M0 -14 V14 M-14 0 H14"/></g>
</svg>`;

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const COUNTDOWN_DESIGNS: CountdownDesign[] = [
  {
    id: "dark",
    name: "Dark Minimal",
    subtitle: "Four-unit blocks",
    widgetType: "countdown",
    category: "countdown",
    description:
      'Dotted charcoal card, "FALTAN" label and four separated unit blocks (D · H · M · S) with mono digits and small labels.',
    swatches: ["#0d0d12", "#1a1a22", "#3ad96b", "#7fbfff"],
    cardWidth: 700,
    cardHeight: 300,
    fonts: ["Inter", "DM Mono"],
    frame: darkFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "flip",
    name: "Flip Board",
    subtitle: "Split-flap units",
    widgetType: "countdown",
    category: "countdown",
    description:
      'Four split-flap tiles with center hinge lines, separators and a "GRAN ESTRENO" header.',
    swatches: ["#15151a", "#26262e", "#f5f5f5", "#ffb703"],
    cardWidth: 720,
    cardHeight: 300,
    fonts: ["DM Mono"],
    frame: flipFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "y2k",
    name: "Y2K Aqua Gel",
    subtitle: "Gel unit pills",
    widgetType: "countdown",
    category: "countdown",
    description:
      "Aqua gel slab with four glossy capsule units, chrome highlights and a green LED counting indicator.",
    swatches: ["#003a7a", "#1c8fff", "#8acfff", "#5aff8a"],
    cardWidth: 720,
    cardHeight: 300,
    fonts: ["Audiowide", "DM Mono"],
    frame: y2kFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "cyber",
    name: "Cyberpunk Neon",
    subtitle: "HUD countdown",
    widgetType: "countdown",
    category: "countdown",
    description:
      'Chamfered HUD with four hex unit cells, neon digits, katakana tag and a "T-MINUS" command line.',
    swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
    cardWidth: 760,
    cardHeight: 300,
    fonts: ["Orbitron", "DM Mono"],
    frame: cyberFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "editorial",
    name: "Editorial",
    subtitle: "Save the date",
    widgetType: "countdown",
    category: "countdown",
    description:
      'Cream invitation card, italic "Reservá la fecha" and four ruled unit columns with serif numerals.',
    swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
    cardWidth: 700,
    cardHeight: 300,
    fonts: ["Cormorant Garamond", "DM Mono"],
    frame: editorialFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "pixel",
    name: "Pixel Arcade",
    subtitle: "Loading screen",
    widgetType: "countdown",
    category: "countdown",
    description:
      '8-bit dialog box with four pixel score-counters, "NEXT EVENT" ribbon and a star sprite.',
    swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
    cardWidth: 720,
    cardHeight: 300,
    fonts: ["Press Start 2P"],
    frame: pixelFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "cottage",
    name: "Cottagecore",
    subtitle: "Calendar pages",
    widgetType: "countdown",
    category: "countdown",
    description:
      'Linen card with cross-stitch border, four torn calendar-page units and italic "falta poquito".',
    swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
    cardWidth: 720,
    cardHeight: 300,
    fonts: ["Cormorant Garamond", "DM Mono"],
    frame: cottageFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "brutalist",
    name: "Brutalist",
    subtitle: "Ledger units",
    widgetType: "countdown",
    category: "countdown",
    description:
      "Black slab with orange bar, four hard-ruled Anton unit cells separated by thin lines.",
    swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
    cardWidth: 720,
    cardHeight: 300,
    fonts: ["Anton", "DM Mono"],
    frame: brutalistFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "anime",
    name: "Anime VHS",
    subtitle: "カウントダウン",
    widgetType: "countdown",
    category: "countdown",
    description:
      "Pink double-stroke card with cyan tracking ghost, katakana corner and four RGB-split unit blocks.",
    swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
    cardWidth: 726,
    cardHeight: 300,
    fonts: ["VT323", "DM Mono"],
    frame: animeFrame,
    slots: TIME_SLOTS,
  },
  {
    id: "holo",
    name: "Holographic Foil",
    subtitle: "Iridescent units",
    widgetType: "countdown",
    category: "countdown",
    description:
      "Pearlescent foil card with refraction streaks, white sparkle and four translucent unit chips.",
    swatches: ["#f0c2ff", "#c2e8ff", "#c2ffd5", "#ffe0c2"],
    cardWidth: 720,
    cardHeight: 300,
    fonts: ["Space Grotesk", "DM Mono"],
    frame: holoFrame,
    slots: TIME_SLOTS,
  },
];

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

/**
 * Render a design's card SVG with the live D/H/M/S substituted in. The
 * `{{DD}}` / `{{HH}}` / `{{MM}}` / `{{SS}}` slots are filled from
 * `parts.dd` / `parts.hh` / `parts.mm` / `parts.ss` via the generic
 * {@link renderDesign} (the single XML-escaping choke-point).
 *
 * Returns a self-contained `<svg>` string (defs ids are already design-prefixed
 * in the catalog) suitable for inlining in the DOM or encoding as an SVG
 * data-URL for a Pixi texture.
 */
export function renderCountdownDesignSvg(
  design: CountdownDesign,
  parts: CountdownParts,
): string {
  return renderDesign(design, {
    values: {
      DD: parts.dd,
      HH: parts.hh,
      MM: parts.mm,
      SS: parts.ss,
    },
  });
}

/**
 * Look up a design by id. First-party catalog wins; when the id is not a
 * shipped design (e.g. a community design the user installed, whose id is a
 * backend id), fall back to the installed-design registry so the live Pixi
 * widget renders it exactly like a first-party one. Returns `undefined` for an
 * absent/unknown id.
 */
export function getCountdownDesign(
  id: string | undefined,
): CountdownDesign | undefined {
  if (!id) {
    return undefined;
  }
  const local = COUNTDOWN_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Community designs are generic SerializableDesigns constrained to the
  // "countdown" widgetType here; the cast is safe (the renderer only consumes
  // the shared shape).
  return getInstalledDesign(id, "countdown") as CountdownDesign | undefined;
}
